import { useState, useCallback } from "react";

interface UseApiState<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
}

interface UseApiReturn<T, A extends any[]> extends UseApiState<T> {
  callApi: (...args: A) => Promise<T | null>;
  reset: () => void;
}

/**
 * Generic API hook
 * @param apiFn - The async API function to call
 * @returns { loading, error, data, callApi, reset }
 */
export function useAPI<T = any, A extends any[] = any[]>(
  apiFn: (...args: A) => Promise<T>
): UseApiReturn<T, A> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<T | null>(null);

  const callApi = useCallback(
    async (...args: A): Promise<T | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiFn(...args);
        setData(result);
        return result;
      } catch (err: any) {
        setError(err?.message || "Something went wrong");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [apiFn]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { loading, error, data, callApi, reset };
}

// Usage
/*
        function MyUploader() {
            const {
                loading,
                error,
                data,
                callApi: completeUpload,
            } = useApi(completeMultipartUpload);

            const handleComplete = async () => {
                await completeUpload({
                uploadId: "123",
                key: "video.mp4",
                parts: [{ ETag: "etag", PartNumber: 1, "content-length": "1024" }],
                authToken: "token_here",
                });
            };

            return (
                <div>
                <button onClick={handleComplete} disabled={loading}>
                    {loading ? "Completing..." : "Complete Upload"}
                </button>
                {error && <p style={{ color: "red" }}>{error}</p>}
                {data && <p>Upload complete ✅</p>}
                </div>
            );
        }
*/
